import React, { useMemo, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { authFetch } from '../../utils/cloudFunctionsAuth';
import { useAppData } from '../../providers/AppDataProvider';

// ---------------------------------------------------------------------------
// Create a negotiated payment arrangement from inside the CRM.
//
// The deal is bound to a CRM customer HERE, so when the client subscribes the
// webhook links the subscription automatically — no review step, and no risk of
// attributing the money to the wrong contact.
//
// The website still owns the record (it mints the Stripe product/prices and the
// client link); we POST through our own function, which holds the shared secret.
// ---------------------------------------------------------------------------

const CF_BASE = 'https://us-central1-web-construct-app.cloudfunctions.net';

type Gst = 'exclusive' | 'inclusive';
interface Line { description: string; qty: string; unitAmount: string }

const money = (n: number) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(n || 0);

/** Mirrors the website's cents math exactly: GST computed ONCE from the totals,
 *  never by summing per-line rounded GST. Keeps this preview honest. */
function totals(lines: Line[], single: string, useLines: boolean, gst: Gst) {
  let sumCents = 0;
  if (useLines) {
    for (const l of lines) {
      const q = Number(l.qty);
      const u = Number(l.unitAmount);
      if (!Number.isFinite(q) || !Number.isFinite(u)) continue;
      sumCents += Math.round(q) * Math.round(u * 100);
    }
  } else {
    const a = Number(single);
    if (Number.isFinite(a)) sumCents = Math.round(a * 100);
  }
  let exC: number; let incC: number;
  if (gst === 'inclusive') { incC = sumCents; exC = Math.round(incC / 1.1); }
  else { exC = sumCents; incC = Math.round(exC * 1.1); }
  return { ex: exC / 100, gst: (incC - exC) / 100, inc: incC / 100 };
}

interface Props {
  show: boolean;
  onClose: () => void;
  onCreated: () => void;
}

const NewArrangementModal: React.FC<Props> = ({ show, onClose, onCreated }) => {
  const { customers } = useAppData();
  const sorted = useMemo(
    () => [...customers].sort((a, b) => (a.company || a.name || '').localeCompare(b.company || b.name || '')),
    [customers],
  );

  const [customerId, setCustomerId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [clientRef, setClientRef] = useState('');
  const [description, setDescription] = useState('Monthly Support Agreement');
  const [interval, setInterval] = useState<'month' | 'year'>('month');
  const [gst, setGst] = useState<Gst>('exclusive');
  const [useLines, setUseLines] = useState(true);
  const [lines, setLines] = useState<Line[]>([{ description: '', qty: '1', unitAmount: '' }]);
  const [single, setSingle] = useState('');
  const [termCycles, setTermCycles] = useState('');
  const [committedTerm, setCommittedTerm] = useState(false);
  const [phase2, setPhase2] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ url: string; id: string } | null>(null);

  const unit = interval === 'year' ? 'years' : 'months';
  const t = totals(lines, single, useLines, gst);
  const t2 = phase2 ? totals([], phase2, false, gst) : null;

  const pickCustomer = (id: string) => {
    setCustomerId(id);
    const c = sorted.find((x) => x.id === id);
    if (c) setCustomerName(c.company || c.name || '');
  };

  const reset = () => {
    setCustomerId(''); setCustomerName(''); setClientRef('');
    setDescription('Monthly Support Agreement'); setInterval('month'); setGst('exclusive');
    setUseLines(true); setLines([{ description: '', qty: '1', unitAmount: '' }]); setSingle('');
    setTermCycles(''); setCommittedTerm(false); setPhase2(''); setResult(null);
  };

  // Closing mid-request must not resurface later as a success panel over a
  // dismissed modal (the arrangement IS still created server-side — we just
  // stop writing state into a closed dialog).
  const closedRef = useRef(false);
  const close = () => { if (busy) return; closedRef.current = true; reset(); onClose(); };

  /** Clearing the term deactivates the step-down + committed controls, so their
   *  values must be cleared too — they were still being submitted. */
  const onTermChange = (v: string) => {
    setTermCycles(v);
    if (!v) { setPhase2(''); setCommittedTerm(false); }
  };

  const submit = async () => {
    if (!customerName.trim()) { toast.warning('Pick a customer (or type a name).'); return; }
    closedRef.current = false; // this dialog is open again — allow state writes
    setBusy(true);
    try {
      const body: any = {
        customerId: customerId || undefined,
        customerName: customerName.trim(),
        clientRef: clientRef.trim() || undefined,
        description: description.trim() || undefined,
        billingInterval: interval,
        gstTreatment: gst,
        termCycles: termCycles ? Number(termCycles) : null,
        committedTerm,
        phase2AmountExGst: phase2 ? Number(phase2) : null,
      };
      if (useLines) {
        const filled = lines.filter((l) => l.description.trim() || l.unitAmount || l.qty !== '1');
        // Validate rather than silently coerce: `qty: Number(l.qty) || 1` used to
        // turn a blank or 0 quantity into 1, so the customer was charged for a
        // line the preview had totalled as $0.
        const bad = filled.find((l) => {
          const q = Number(l.qty);
          return !l.description.trim()
            || !Number.isInteger(q) || q < 1 || q > 999
            || !(Number(l.unitAmount) > 0);
        });
        if (bad) {
          toast.warning('Every line needs a description, a whole quantity of 1 or more, and an amount above zero.');
          setBusy(false);
          return;
        }
        body.lineItems = filled.map((l) => ({
          description: l.description.trim(), qty: Number(l.qty), unitAmount: Number(l.unitAmount),
        }));
        if (body.lineItems.length === 0) { toast.warning('Add at least one line item.'); setBusy(false); return; }
      } else {
        body.phase1AmountExGst = Number(single);
      }

      const res = await authFetch(`${CF_BASE}/createArrangementHttp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      // The endpoint only exists once the shared arrangements secret is set on
      // both sides — say so plainly instead of failing on a JSON parse.
      if (res.status === 404) {
        toast.error('Creating arrangements from the app isn\'t switched on yet — the shared arrangements secret still needs configuring.');
        setBusy(false);
        return;
      }
      const data = await res.json().catch(() => ({ ok: false, error: 'Unexpected response from the server' }));
      if (!data.ok) { toast.error(data.error || 'Could not create the arrangement'); setBusy(false); return; }
      if (closedRef.current) return;
      setResult({ url: data.url, id: data.id });
      toast.success('Arrangement created — send the link to your client.');
      onCreated();
    } catch (e: any) {
      toast.error(e?.message || 'Could not create the arrangement');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal show={show} onHide={close} centered size="lg" backdrop="static">
      <Modal.Header closeButton={!busy}>
        <Modal.Title className="fs-4 fw-bolder">New payment arrangement</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {result ? (
          <div className="text-center py-4">
            <i className="bi bi-check-circle-fill text-success" style={{ fontSize: 44 }}></i>
            <div className="fw-bolder fs-4 mt-3 mb-1">Arrangement created</div>
            <div className="text-muted fs-7 mb-4">
              Send this link to {customerName}. When they subscribe it links to their CRM record
              automatically and every payment is invoiced.
            </div>
            <div className="d-flex gap-2">
              <input readOnly className="form-control" value={result.url} onFocus={(e) => e.currentTarget.select()} />
              <button
                className="btn btn-primary text-nowrap"
                onClick={async () => {
                  try { await navigator.clipboard.writeText(result.url); toast.success('Link copied'); }
                  catch { toast.error('Could not copy — select the link and copy it manually'); }
                }}
              >
                <i className="fas fa-copy me-1"></i>Copy
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Customer */}
            <div className="row g-3 mb-4">
              <div className="col-md-6">
                <label className="form-label fs-8 fw-bold text-muted text-uppercase">CRM customer</label>
                <select className="form-select" value={customerId} onChange={(e) => pickCustomer(e.target.value)}>
                  <option value="">Not in the CRM yet…</option>
                  {sorted.map((c) => (
                    <option key={c.id} value={c.id}>{c.company || c.name}</option>
                  ))}
                </select>
                <div className="form-text fs-9">
                  {customerId
                    ? 'The subscription will link to this contact automatically.'
                    : 'Without a contact you\'ll link the subscription manually later.'}
                </div>
              </div>
              <div className="col-md-6">
                <label className="form-label fs-8 fw-bold text-muted text-uppercase">Name shown to the client</label>
                <input className="form-control" value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Acme Pty Ltd" />
              </div>
              <div className="col-md-8">
                <label className="form-label fs-8 fw-bold text-muted text-uppercase">Description</label>
                <input className="form-control" value={description} onChange={(e) => setDescription(e.target.value)} />
              </div>
              <div className="col-md-4">
                <label className="form-label fs-8 fw-bold text-muted text-uppercase">Reference (optional)</label>
                <input className="form-control" value={clientRef} onChange={(e) => setClientRef(e.target.value)} />
              </div>
            </div>

            {/* Billing basis */}
            <div className="row g-3 mb-4">
              <div className="col-md-4">
                <label className="form-label fs-8 fw-bold text-muted text-uppercase">Billed every</label>
                <select className="form-select" value={interval} onChange={(e) => setInterval(e.target.value as any)}>
                  <option value="month">Month</option>
                  <option value="year">Year</option>
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label fs-8 fw-bold text-muted text-uppercase">Amounts are</label>
                <select className="form-select" value={gst} onChange={(e) => setGst(e.target.value as Gst)}>
                  <option value="exclusive">GST exclusive</option>
                  <option value="inclusive">GST inclusive</option>
                </select>
              </div>
              <div className="col-md-4 d-flex align-items-end">
                <div className="form-check form-switch">
                  <input className="form-check-input" type="checkbox" id="useLines" checked={useLines} onChange={(e) => setUseLines(e.target.checked)} />
                  <label className="form-check-label fs-7" htmlFor="useLines">Itemise the opening rate</label>
                </div>
              </div>
            </div>

            {/* Opening rate */}
            <div className="mb-4">
              <div className="fw-bold text-gray-800 fs-6 mb-2">Opening rate</div>
              {useLines ? (
                <>
                  {lines.map((l, i) => (
                    <div className="row g-2 mb-2" key={i}>
                      <div className="col-6">
                        <input className="form-control form-control-sm" placeholder="Description" value={l.description}
                          onChange={(e) => setLines((p) => p.map((x, j) => j === i ? { ...x, description: e.target.value } : x))} />
                      </div>
                      <div className="col-2">
                        <input className="form-control form-control-sm" type="number" min={1} placeholder="Qty" value={l.qty}
                          onChange={(e) => setLines((p) => p.map((x, j) => j === i ? { ...x, qty: e.target.value } : x))} />
                      </div>
                      <div className="col-3">
                        <input className="form-control form-control-sm" type="number" step="0.01" placeholder="Unit $" value={l.unitAmount}
                          onChange={(e) => setLines((p) => p.map((x, j) => j === i ? { ...x, unitAmount: e.target.value } : x))} />
                      </div>
                      <div className="col-1">
                        <button className="btn btn-sm btn-light-danger w-100" disabled={lines.length === 1}
                          onClick={() => setLines((p) => p.filter((_, j) => j !== i))}>×</button>
                      </div>
                    </div>
                  ))}
                  <button className="btn btn-sm btn-light-primary" disabled={lines.length >= 20}
                    onClick={() => setLines((p) => [...p, { description: '', qty: '1', unitAmount: '' }])}>
                    <i className="bi bi-plus-lg me-1" />Add line
                  </button>
                </>
              ) : (
                <div className="col-md-4">
                  <input className="form-control" type="number" step="0.01" placeholder="Amount per cycle"
                    value={single} onChange={(e) => setSingle(e.target.value)} />
                </div>
              )}

              <div className="mt-3 p-3 rounded bg-light d-flex flex-wrap gap-4">
                <div><div className="text-muted fs-9">Subtotal (ex GST)</div><div className="fw-bold">{money(t.ex)}</div></div>
                <div><div className="text-muted fs-9">GST</div><div className="fw-bold">{money(t.gst)}</div></div>
                <div><div className="text-muted fs-9">Charged per {interval}</div><div className="fw-bolder fs-4 text-success">{money(t.inc)}</div></div>
              </div>
            </div>

            {/* Term + step-down */}
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label fs-8 fw-bold text-muted text-uppercase">Opening rate runs for</label>
                <div className="input-group">
                  <input className="form-control" type="number" min={1} max={120} placeholder="e.g. 12"
                    value={termCycles} onChange={(e) => onTermChange(e.target.value)} />
                  <span className="input-group-text">{unit}</span>
                </div>
                <div className="form-text fs-9">Leave blank for ongoing at one rate.</div>
              </div>
              <div className="col-md-4">
                <label className="form-label fs-8 fw-bold text-muted text-uppercase">
                  Then ({gst === 'inclusive' ? 'inc' : 'ex'} GST)
                </label>
                <input className="form-control" type="number" step="0.01" placeholder="e.g. 90"
                  value={phase2} onChange={(e) => setPhase2(e.target.value)} disabled={!termCycles} />
                {t2 && <div className="form-text fs-9">Charged {money(t2.inc)}/{interval}</div>}
              </div>
              <div className="col-md-4 d-flex align-items-center">
                <div className="form-check form-switch mt-4">
                  <input className="form-check-input" type="checkbox" id="committed" checked={committedTerm}
                    disabled={!termCycles} onChange={(e) => setCommittedTerm(e.target.checked)} />
                  <label className="form-check-label fs-7" htmlFor="committed">
                    Committed minimum term
                  </label>
                </div>
              </div>
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <button className="btn btn-light" onClick={close} disabled={busy}>{result ? 'Done' : 'Cancel'}</button>
        {!result && (
          <button className="btn btn-primary" onClick={submit} disabled={busy}>
            {busy
              ? <><span className="spinner-border spinner-border-sm me-2"></span>Creating…</>
              : <>Create &amp; get link</>}
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default NewArrangementModal;
